import { Content as AntContent } from "antd/es/layout/layout";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import styled from "styled-components";
import { OrdersHeader } from "../organisms/orders-header/OrdersHeader";
import { OrdersList } from "../organisms/orders-list/OrdersList";
import { Col, Row, Tabs } from "antd";
import { useResponsive } from "../../hooks/useResponsive";
import { CreateOrderModal } from "../molecules/create-order-modal/CreateOrderModal";
import { colors, spacing } from "../../styles/theme";
import { useBranchContext } from "../../contexts/branch-context";
import { TablesOrdersModal } from "../molecules/tables-orders-modal/TablesOrdersModal";
import { OrdersEmptyState } from "../organisms/orders-empty-state/OrdersEmptyState";
import { useQuery } from "react-query";
import { getProductCategories, getProductOrders } from "../../service/client";

export const Content = styled(AntContent)`
  padding: ${spacing.sm};
  min-height: 100%;
  max-height: 100%;
  height: 100%;
  margin: 0;
`;

export const Orders: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const [height, setHeight] = useState(0);
  const [showDone, setShowDone] = useState(false);

  // Inicjalizuj filtry z localStorage
  const [filters, setFilters] = useState<any>(() => {
    try {
      const savedFilters = localStorage.getItem('ordersFilters');
      return savedFilters ? JSON.parse(savedFilters) : {};
    } catch {
      return {};
    }
  });

  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState("IN_QUEUE");
  const [createOrderModalVisible, setCreateOrderModalVisible] = useState(false);
  const [showTablesModal, setShowTablesModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);

  // Inicjalizuj viewMode z localStorage
  const [viewMode, setViewMode] = useState<"realizowane" | "kuchnia" | "dostawa" | "wszystkie">(() => {
    try {
      const savedViewMode = localStorage.getItem('ordersViewMode');
      return (savedViewMode as any) || "realizowane";
    } catch {
      return "realizowane";
    }
  });

  const sellType = currentBranch?.sellType ?? "PRODUCTS_SELLER";

  // Sprawdź czy są produkty w menu (empty state)
  const { data: productCategories, isLoading: categoriesLoading } = useQuery(
    ["/product-categories"],
    () => getProductCategories(),
    { enabled: sellType === "PRODUCTS_SELLER" }
  );
  const hasProducts = productCategories?.some(
    (cat: any) => cat.productBases?.length > 0
  );
  const showEmptyState = !categoriesLoading && sellType === "PRODUCTS_SELLER" && !hasProducts;

  // Sprawdź czy branch kiedykolwiek miał zamówienie (ukryj kontrolki jeśli nie)
  const { data: doneOrders, isLoading: doneOrdersLoading } = useQuery(
    ["/product-orders/DONE", currentBranch?.id],
    () => getProductOrders({ id: currentBranch!.id, status: "DONE", filters: {} }),
    { enabled: !!currentBranch?.id && sellType === "PRODUCTS_SELLER", staleTime: 5 * 60 * 1000 }
  );
  const hasPastOrders = (doneOrders?.length ?? 0) > 0;
  const hideOrderControls = !doneOrdersLoading && sellType === "PRODUCTS_SELLER" && !hasPastOrders;

  // Zapisz filtry do localStorage przy każdej zmianie
  useEffect(() => {
    try {
      localStorage.setItem('ordersFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  }, [filters]);

  // Zapisz viewMode do localStorage przy każdej zmianie
  useEffect(() => {
    try {
      localStorage.setItem('ordersViewMode', viewMode);
    } catch (error) {
      console.error('Failed to save viewMode to localStorage:', error);
    }
  }, [viewMode]);

  const handleEditOrder = (orderData: any) => {
    setEditingOrder(orderData);
    setCreateOrderModalVisible(true);
  };

  // Definicja statusów zamówień z etykietami
  // Definicja statusów zamówień z etykietami
  const orderStatuses = [
    { key: "IN_QUEUE", label: t("orders.status.inQueue") },
    { key: "IN_PROGRESS", label: t("orders.status.inProgress") },
    { key: "IN_DELIVERY", label: t("orders.status.inDelivery") },
    { key: "DONE", label: t("orders.status.done") },
  ];

  // Renderowanie mobilne - zakładki
  // Renderowanie mobilne
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: "100%",
        }}
      >
        <OrdersHeader
          setHeight={setHeight}
          setShowDone={setShowDone}
          showDone={showDone}
          setFilters={setFilters}
          filters={filters}
          onCreateOrder={() => setCreateOrderModalVisible(true)}
          onShowTablesView={() => setShowTablesModal(true)}
          disableActions={showEmptyState}
          hideOrderControls={hideOrderControls || showEmptyState}
          mobileTabs={sellType === "PRODUCTS_SELLER" && !showEmptyState ? orderStatuses : undefined}
          mobileActiveTab={activeTab}
          onMobileTabChange={setActiveTab}
        />
        <div
          style={{
            flex: "1 1 auto",
            overflow: "auto",
          }}
        >
          {showEmptyState ? (
            <OrdersEmptyState type="orders" />
          ) : sellType === "PRODUCTS_SELLER" ? (
            <OrdersList
              height={height}
              status={activeTab}
              selectedBranch={currentBranch}
              filters={filters}
              onEditOrder={handleEditOrder}
            />
          ) : (
            <OrdersList
              height={height}
              status="DONE"
              selectedBranch={currentBranch}
              filters={filters}
              onEditOrder={handleEditOrder}
            />
          )}
        </div>
        <CreateOrderModal
          visible={createOrderModalVisible}
          onClose={() => {
            setCreateOrderModalVisible(false);
            setEditingOrder(null);
          }}
          selectedBranch={currentBranch}
          editMode={!!editingOrder}
          existingOrder={editingOrder}
        />
        <TablesOrdersModal
          visible={showTablesModal}
          onClose={() => setShowTablesModal(false)}
          selectedBranch={currentBranch}
        />
      </div>
    );
  }

  // Funkcja określająca które kolumny pokazać w zależności od viewMode
  const getVisibleColumns = () => {
    if (sellType === "DIGITAL_PRODUCTS_SELLER") {
      return ["DONE"];
    }

    switch (viewMode) {
      case "kuchnia":
        return ["IN_QUEUE", "IN_PROGRESS"];
      case "dostawa":
        return ["IN_DELIVERY"];
      case "wszystkie":
        return ["IN_QUEUE", "IN_PROGRESS", "IN_DELIVERY", "DONE"];
      case "realizowane":
      default:
        return ["IN_QUEUE", "IN_PROGRESS", "IN_DELIVERY"];
    }
  };

  const visibleColumns = getVisibleColumns();
  const columnSpan = 24 / visibleColumns.length;

  // Renderowanie desktop/tablet - kolumny
  return (
    <div
      style={{
        display: "flex",
        flexFlow: "column",
        height: "100%",
        maxHeight: "100%",
        minHeight: "100%",
      }}
    >
      <OrdersHeader
        setHeight={setHeight}
        setShowDone={setShowDone}
        showDone={showDone}
        setFilters={setFilters}
        filters={filters}
        onCreateOrder={() => setCreateOrderModalVisible(true)}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onShowTablesView={() => setShowTablesModal(true)}
        disableActions={showEmptyState}
        hideOrderControls={hideOrderControls || showEmptyState}
      />
      <div
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          width: "100%",
        }}
      >
        {showEmptyState ? (
          <OrdersEmptyState type="orders" />) :
          <Row
            gutter={8}
            style={{
              padding: spacing.md,
            }}
          >
            {visibleColumns.includes("IN_QUEUE") && (
              <Col span={columnSpan}>
                <OrdersList
                  height={height}
                  status="IN_QUEUE"
                  selectedBranch={currentBranch}
                  filters={filters}
                  onEditOrder={handleEditOrder}
                />
              </Col>
            )}
            {visibleColumns.includes("IN_PROGRESS") && (
              <Col span={columnSpan}>
                <OrdersList
                  height={height}
                  status="IN_PROGRESS"
                  selectedBranch={currentBranch}
                  filters={filters}
                  onEditOrder={handleEditOrder}
                />
              </Col>
            )}
            {visibleColumns.includes("IN_DELIVERY") && (
              <Col span={columnSpan}>
                <OrdersList
                  height={height}
                  status="IN_DELIVERY"
                  selectedBranch={currentBranch}
                  filters={filters}
                  onEditOrder={handleEditOrder}
                />
              </Col>
            )}
            {visibleColumns.includes("DONE") && (
              <Col span={columnSpan}>
                <OrdersList
                  height={height}
                  status="DONE"
                  selectedBranch={currentBranch}
                  filters={filters}
                  onEditOrder={handleEditOrder}
                />
              </Col>
            )}
          </Row>}
      </div>
      <CreateOrderModal
        visible={createOrderModalVisible}
        onClose={() => {
          setCreateOrderModalVisible(false);
          setEditingOrder(null);
        }}
        selectedBranch={currentBranch}
        editMode={!!editingOrder}
        existingOrder={editingOrder}
      />
      <TablesOrdersModal
        visible={showTablesModal}
        onClose={() => setShowTablesModal(false)}
        selectedBranch={currentBranch}
      />
    </div>
  );
};
